import React, { useState, useCallback, useEffect } from 'react';
import { coHostService } from '../services';
import friendService from '../services/friendService';
import { useDialog } from '../context';
import { SubPanel, Inner, Row, RowText, Btn, TextInput, SearchPill, SegPills, ToggleRow, Ava, Tag, Help, ErrorLine } from './host/kit';

/**
 * CoHostsPanel — list + manage co-hosts on an event. Works on draft events
 * (the add-cohost API has no status gate), so it's rendered by the event wizard
 * (Step 4 EXTRAS · CO-HOSTS of docs/plans/2026-09-host-event-edit) on both
 * create and edit. Built on the host kit: a grey sub-panel, white round rows,
 * and an always-open add card for the primary host.
 *
 * Split storage convention (matches backend): basis points.
 *   percent     → 0..10000 (10000 = 100%)
 *   fixed_cents → 0..N     (cents)
 */
export default function CoHostsPanel({ eventId, isPrimaryHost }) {
  const { showConfirm } = useDialog();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    const r = await coHostService.list(eventId);
    if (r.success) setRows(r.data || []);
    setLoading(false);
  }, [eventId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // `25% OF EVERY SALE` · `$20.00 PER TICKET` · `NO SPLIT`
  const splitMeta = (row) => {
    const bp = Number(row.splitValueBasisPoints) || 0;
    if (bp <= 0) return 'NO SPLIT';
    if (row.splitType === 'percent') return `${(bp / 100).toFixed(bp % 100 ? 2 : 0)}% OF EVERY SALE`;
    return `$${(bp / 100).toFixed(2)} PER TICKET`;
  };

  const handleRemove = async (row) => {
    const ok = await showConfirm({
      title: 'REMOVE CO-HOST',
      message: `Remove ${row.displayName} as a co-host? Their existing earnings stay; they just won't receive any further splits.`,
      confirmLabel: 'REMOVE',
      variant: 'danger',
    });
    if (!ok) return;
    const r = await coHostService.remove(eventId, row.id);
    if (r.success) refresh();
    else setError(r.error || 'Could not remove co-host');
  };

  // Primary host grants/revokes a co-host's permission to edit the event.
  const handleToggleEdit = async (row) => {
    setError(null);
    const next = !row.canEdit;
    const r = await coHostService.setPermission(eventId, row.id, next);
    if (r.success) setRows((rs) => rs.map((x) => (x.id === row.id ? { ...x, canEdit: next } : x)));
    else setError(r.error || 'Could not update edit permission');
  };

  return (
    <SubPanel
      title="CO-HOSTS"
      subBody
      sub={(
        <>
          Co-hosts receive a share of every ticket sale on this event. Splits apply at escrow release. The primary host always gets whatever’s left. Grant <b>CAN EDIT</b> to let a co-host change the event’s details; only you can manage co-hosts, splits, and these permissions.
        </>
      )}
      testId="co-hosts-panel"
    >
      {loading ? (
        <Help>Loading…</Help>
      ) : rows.length === 0 ? (
        <Help>No co-hosts on this event yet.</Help>
      ) : (
        rows.map((row) => (
          <Row key={row.id} round pad="8" testId={`co-host-${row.id}`}>
            <Ava square size={36} name={row.displayName} imageUrl={row.avatarUrl} tintKey={row.userId || row.id} />
            <RowText title={row.displayName} meta={splitMeta(row)} />
            {isPrimaryHost ? (
              <button
                type="button"
                className="hev-tag"
                data-black={row.canEdit ? '' : undefined}
                data-small=""
                onClick={() => handleToggleEdit(row)}
                aria-pressed={!!row.canEdit}
                title={row.canEdit ? 'Can edit the event — tap to make view-only' : 'View only — tap to allow editing the event'}
                style={{ border: 'none', cursor: 'pointer', fontFamily: 'inherit', transition: 'background-color .2s, color .2s' }}
              >
                {row.canEdit ? 'CAN EDIT' : 'VIEW ONLY'}
              </button>
            ) : (
              row.canEdit && <Tag black small>CAN EDIT</Tag>
            )}
            {isPrimaryHost && (
              <button
                type="button"
                onClick={() => handleRemove(row)}
                style={{ flexShrink: 0, height: 32, padding: '0 12px', border: 'none', borderRadius: 999, background: '#F5F5F5', color: '#DC2626', fontFamily: 'inherit', fontSize: 9, fontWeight: 700, letterSpacing: '.12em', cursor: 'pointer' }}
              >
                REMOVE
              </button>
            )}
          </Row>
        ))
      )}

      <ErrorLine>{error}</ErrorLine>

      {isPrimaryHost && (
        <AddCoHostForm
          eventId={eventId}
          onAdded={() => { setError(null); refresh(); }}
          onError={setError}
        />
      )}
    </SubPanel>
  );
}

const SPLIT_TYPES = [
  { key: 'percent', label: 'PERCENT' },
  { key: 'fixed_cents', label: 'FIXED $ / TICKET' },
];

const label9 = { fontSize: 9, fontWeight: 700, letterSpacing: '.16em', textTransform: 'uppercase' };

function AddCoHostForm({ eventId, onAdded, onError }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);   // { userId, displayName, handle, avatarUrl }
  const [splitType, setSplitType] = useState('percent');
  const [splitInput, setSplitInput] = useState('');
  const [canEdit, setCanEdit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hover, setHover] = useState(null);      // userId of the result pill under the pointer

  useEffect(() => {
    if (!search.trim() || search.length < 2 || picked) {
      setResults([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      const r = await friendService.searchPeople(search);
      if (r.success) setResults(r.data || []);
      setSearching(false);
    }, 300);
    return () => clearTimeout(t);
  }, [search, picked]);

  const handleSubmit = async () => {
    if (!picked) {
      onError?.('Pick a person first');
      return;
    }
    const raw = parseFloat(splitInput);
    if (isNaN(raw) || raw < 0) {
      onError?.('Enter a valid split');
      return;
    }
    const splitValueBasisPoints = splitType === 'percent'
      ? Math.round(raw * 100)            // e.g. 25 → 2500 basis points
      : Math.round(raw * 100);           // e.g. $20.00 → 2000 cents
    setSubmitting(true);
    const r = await coHostService.add(eventId, {
      userId: picked.userId,
      splitType,
      splitValueBasisPoints,
      canEdit,
    });
    setSubmitting(false);
    if (r.success) {
      setPicked(null);
      setSplitInput('');
      setSearch('');
      setCanEdit(false);
      onAdded?.();
    } else {
      onError?.(r.error || 'Failed to add co-host');
    }
  };

  return (
    <Inner testId="co-host-add">
      <span style={label9}>FIND CO-HOST</span>
      {picked ? (
        <Row round pad="6" style={{ background: '#000', color: '#fff' }} testId="co-host-picked">
          <Ava size={30} name={picked.displayName} imageUrl={picked.avatarUrl} tintKey={picked.userId} />
          <span style={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {picked.displayName}
            {picked.handle && <span style={{ fontWeight: 500, color: '#9CA3AF' }}> @{picked.handle}</span>}
          </span>
          <button
            type="button"
            onClick={() => setPicked(null)}
            style={{ flexShrink: 0, height: 28, padding: '0 12px', border: 'none', borderRadius: 999, background: '#fff', color: '#000', fontFamily: 'inherit', fontSize: 9, fontWeight: 700, letterSpacing: '.12em', cursor: 'pointer' }}
          >
            CHANGE
          </button>
        </Row>
      ) : (
        <>
          <SearchPill
            small
            placeholder="Search by name or @handle…"
            aria-label="Find co-host"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {searching && <Help>Searching…</Help>}
          {results.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }} data-testid="co-host-results">
              {results.map((person) => {
                const hot = hover === person.userId;
                return (
                  <button
                    key={person.userId}
                    type="button"
                    onClick={() => { setHover(null); setPicked(person); }}
                    onMouseEnter={() => setHover(person.userId)}
                    onMouseLeave={() => setHover(null)}
                    onFocus={() => setHover(person.userId)}
                    onBlur={() => setHover(null)}
                    title={person.handle ? `@${person.handle}` : undefined}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, height: 36, padding: '0 14px 0 4px', border: 'none', borderRadius: 999, background: hot ? '#000' : '#F5F5F5', color: hot ? '#fff' : '#000', fontFamily: 'inherit', fontSize: 11, fontWeight: 600, cursor: 'pointer', transition: 'background-color .2s, color .2s', maxWidth: '100%' }}
                  >
                    <Ava size={28} name={person.displayName} imageUrl={person.avatarUrl} tintKey={person.userId} />
                    <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{person.displayName}</span>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      <span style={label9}>SPLIT</span>
      <span style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        <SegPills small ariaLabel="Split type" options={SPLIT_TYPES} value={splitType} onChange={setSplitType} />
        <TextInput
          size="xs"
          type="number"
          step="0.01"
          min="0"
          max={splitType === 'percent' ? 100 : 100000}
          value={splitInput}
          onChange={(e) => setSplitInput(e.target.value)}
          placeholder={splitType === 'percent' ? 'e.g. 25' : 'e.g. 20.00'}
          aria-label={splitType === 'percent' ? 'Percent of every sale' : 'Dollars per ticket'}
          style={{ width: 120 }}
        />
      </span>

      <span style={label9}>PERMISSIONS</span>
      <ToggleRow
        white
        plain
        label="Allow this co-host to edit the event"
        on={canEdit}
        onToggle={() => setCanEdit((v) => !v)}
        style={{ background: '#F5F5F5' }}
      />

      <Btn full disabled={submitting || !picked || !splitInput} onClick={handleSubmit}>
        {submitting ? 'ADDING…' : 'ADD CO-HOST'}
      </Btn>
    </Inner>
  );
}
